// dashboardThunks.ts
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getPreEmploymentData,
  getUserEngagementData,
  getUserCourseData,
  getOrganizationUserStastics,
  getOrganizaationAllUserSwotReport,
  getSwotReportAsPerRegionAndAge,
  getPreAssessmentReportByRegion,
} from './organization-api';

// ----------- Simple cache -----------
const cache: Map<string, unknown> = new Map();

function makeKey(prefix: string, params: Record<string, unknown>): string {
  return `${prefix}:${JSON.stringify(params)}`;
}

// ----------- Thunks with caching -----------

export const fetchPreEmployment = createAsyncThunk(
  'organization/fetchPreEmployment',
  async ({ organizationId, limit }: { organizationId: string; limit: number }) => {
    const key = makeKey('preEmployment', { organizationId, limit });
    if (cache.has(key)) return cache.get(key);

    const data = await getPreEmploymentData(organizationId, limit);
    cache.set(key, data);
    return data;
  },
);

export const fetchEngagement = createAsyncThunk(
  'organization/fetchEngagement',
  async ({ organizationId, limit }: { organizationId: string; limit: number }) => {
    const key = makeKey('engagement', { organizationId, limit });
    if (cache.has(key)) return cache.get(key);

    const data = await getUserEngagementData(organizationId, limit);
    cache.set(key, data);
    return data;
  },
);

export const fetchCourses = createAsyncThunk(
  'organization/fetchCourses',
  async ({ organizationId, limit }: { organizationId: string; limit: number }) => {
    const key = makeKey('courses', { organizationId, limit });
    if (cache.has(key)) return cache.get(key);

    const data = await getUserCourseData(organizationId, limit);
    cache.set(key, data);
    return data;
  },
);

export const fetchStats = createAsyncThunk(
  'organization/fetchStats',
  async ({ organizationId, limit }: { organizationId: string; limit: number }) => {
    const key = makeKey('stats', { organizationId, limit });
    if (cache.has(key)) return cache.get(key);

    const data = await getOrganizationUserStastics(organizationId, limit);
    cache.set(key, data);
    return data;
  },
);

export const fetchSwot = createAsyncThunk(
  'organization/fetchSwot',
  async ({
    organizationId,
    startDate,
    endDate,
  }: {
    organizationId: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const key = makeKey('swot', { organizationId, startDate, endDate });
    if (cache.has(key)) return cache.get(key);

    const data = await getOrganizaationAllUserSwotReport(organizationId, startDate, endDate);
    cache.set(key, data);
    return data;
  },
);

export const fetchSwotReportAsPerAgeAndRegion = createAsyncThunk(
  'organization/fetchSwotReportAsPerAgeAndRegion',
  async ({
    organizationId,
    startDate,
    endDate,
  }: {
    organizationId: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const key = makeKey('swotAgeRegion', { organizationId, startDate, endDate });
    if (cache.has(key)) return cache.get(key);

    const data = await getSwotReportAsPerRegionAndAge(organizationId, startDate, endDate);
    cache.set(key, data);
    return data;
  },
);

export const fetchPreAssessmentReportAsPerRegion = createAsyncThunk(
  'organization/fetchPreAssessmentReportAsPerRegion',
  async ({
    organizationId,
    startDate,
    endDate,
  }: {
    organizationId: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const key = makeKey('preAssessmentRegion', { organizationId, startDate, endDate });
    if (cache.has(key)) return cache.get(key);

    const data = await getPreAssessmentReportByRegion(organizationId, startDate, endDate);
    cache.set(key, data);
    return data;
  },
);
